/**
 * Sprint-5 — top-of-step progress widget for the consent flow.
 *
 * Three coloured dots reflecting the three stages of the signing flow
 * (single subject signer, not a multi-role chain):
 *
 *   1. Identify  — coordinator filled identity verification
 *   2. Sign      — embed open (in-person) or PIN+link sent (remote)
 *   3. Confirmed — DocuSeal webhook flipped signed_at
 *
 * Stages are passed in by the parent, who has the authoritative state.
 */
import { Check } from 'lucide-react';

export type SigningStage = 'identify' | 'sign' | 'confirmed';
export type StageState = 'pending' | 'active' | 'completed';

export interface SigningProgressProps {
  /** Map of stage -> current state. Parent computes from local flow state. */
  stages: Record<SigningStage, StageState>;
  /** Optional override for the labels (default: three standard signing-stage labels). */
  labels?: Partial<Record<SigningStage, string>>;
}

const DEFAULT_LABELS: Record<SigningStage, string> = {
  identify: 'Identify',
  sign: 'Sign',
  confirmed: 'Confirmed',
};

const ORDER: SigningStage[] = ['identify', 'sign', 'confirmed'];

export function SigningProgress({ stages, labels }: SigningProgressProps) {
  const completedCount = ORDER.filter((s) => stages[s] === 'completed').length;
  const total = ORDER.length;

  return (
    <div className="flex items-center justify-between gap-3 rounded border border-surface-border bg-surface-card px-4 py-3">
      <ol className="flex flex-1 items-center gap-2">
        {ORDER.map((stage, i) => {
          const state = stages[stage];
          const label = labels?.[stage] ?? DEFAULT_LABELS[stage];
          return (
            <li key={stage} className="flex flex-1 items-center gap-2">
              <Dot state={state} index={i + 1} />
              <span
                className={
                  state === 'active'
                    ? 'text-xs font-medium text-ink-primary'
                    : state === 'completed'
                      ? 'text-xs font-medium text-emerald-700'
                      : 'text-xs text-ink-muted'
                }
              >
                {label}
              </span>
              {i < ORDER.length - 1 && (
                <span className={`mx-1 flex-1 border-t ${stages[ORDER[i + 1]!] === 'pending' ? 'border-surface-border' : 'border-accent-400'}`} />
              )}
            </li>
          );
        })}
      </ol>
      <div className="text-[11px] font-medium text-ink-muted">
        {completedCount}/{total} completed
      </div>
    </div>
  );
}

function Dot({ state, index }: { state: StageState; index: number }) {
  if (state === 'completed') {
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-white">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-600 text-xs font-semibold text-white ring-2 ring-accent-200">
        {index}
      </span>
    );
  }
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-surface-border bg-surface-muted text-xs font-semibold text-ink-muted">
      {index}
    </span>
  );
}
